// Editable per-(role × skill) requirement cell used in Create-Project
// mode (flat view only — grouped cells stay read-only aggregates).
// Click to open a 4-option proficiency picker; the chosen level paints
// the cell in the corresponding teal shade. Clearing the level returns
// it to the empty placeholder.

import type { RatingValue } from '@lib/api-types/ratings';
import { useEffect, useRef, useState } from 'react';


import { RATING_LABELS } from '../../data-views.ts';

const RATING_LETTERS: Record<RatingValue, string> = {
  1: 'A',
  2: 'C',
  3: 'P',
  4: 'E',
};

const LEVELS: RatingValue[] = [1, 2, 3, 4];

/** Cell selection state. `'deprecated'` reuses the snapshot-wide
 *  `is-deprecated` diagonal-stripe styling (see tokens.css), the same
 *  treatment Timeline gives removed skills / comps / functions. */
export type RequirementValue = RatingValue | 'deprecated' | null;

export function RequirementCell({
  level,
  isOverlay,
  gridRow,
  gridColumn,
  onSet,
  availableFunctions,
  assignedFunctionIds,
  onToggleFunction,
}: {
  level: RequirementValue;
  isOverlay: boolean;
  gridRow: number;
  gridColumn: number;
  onSet: (next: RequirementValue) => void;
  /** Full function list shown in the "Assign functions" sub-view. */
  availableFunctions?: Array<{ id: string; display: string }>;
  /** Set of function ids currently assigned to this cell. Includes the
   *  default-from-intersect set when the user hasn't customised yet. */
  assignedFunctionIds?: Set<string>;
  /** Toggle a single function's assignment on/off. */
  onToggleFunction?: (functionId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<'levels' | 'functions'>('levels');
  const wrapRef = useRef<HTMLDivElement>(null);

  // Reset to the level view every time the popover closes so re-opens
  // start from the primary menu, not whatever sub-view the user left it
  // on last time.
  useEffect(() => {
    if (!open) setView('levels');
  }, [open]);

  // Click-away closes the picker. mousedown rather than click so the
  // outside listener fires before any inside-button click handler.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const overlayClass = isOverlay ? ' is-overlay-cell' : '';
  const hasNumericLevel = level !== null && level !== 'deprecated';
  const stateClass =
    level === 'deprecated'
      ? 'is-deprecated'
      : level
        ? `teal lvl-${level}`
        : 'empty';
  const title =
    level === 'deprecated'
      ? 'Deprecated'
      : level
        ? RATING_LABELS[level]
        : 'Set required proficiency';

  return (
    <div
      ref={wrapRef}
      className='snap-req-cell'
      style={{ gridRow, gridColumn }}
    >
      <button
        type='button'
        className={`snap-cell req ${stateClass}${overlayClass} snap-req-cell__btn`}
        onClick={() => setOpen(v => !v)}
        aria-haspopup='true'
        aria-expanded={open}
        title={title}
      >
        {level && level !== 'deprecated' ? RATING_LETTERS[level] : ''}
      </button>
      {open ? (
        <div className='snap-req-picker' role='menu'>
          {view === 'levels' ? (
            <>
              {/* "Assign functions" only surfaces once a numeric level is
               *  set — for empty / deprecated cells the function scoping
               *  is meaningless (nothing to scope). */}
              {hasNumericLevel && availableFunctions && onToggleFunction ? (
                <button
                  type='button'
                  className='snap-req-picker__opt snap-req-picker__opt--functions'
                  role='menuitem'
                  onClick={() => setView('functions')}
                >
                  <span className='snap-req-picker__swatch is-functions' aria-hidden>
                    <svg
                      viewBox='0 0 24 24'
                      fill='none'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    >
                      <rect x='3' y='3' width='7' height='7' />
                      <rect x='14' y='3' width='7' height='7' />
                      <rect x='3' y='14' width='7' height='7' />
                      <rect x='14' y='14' width='7' height='7' />
                    </svg>
                  </span>
                  <span>Assign functions…</span>
                </button>
              ) : null}
              {LEVELS.map(lvl => (
                <button
                  key={lvl}
                  type='button'
                  className='snap-req-picker__opt'
                  role='menuitem'
                  onClick={() => {
                    onSet(lvl);
                    setOpen(false);
                  }}
                >
                  <span
                    className={`snap-req-picker__swatch snap-cell teal lvl-${lvl}`}
                    aria-hidden
                  />
                  <span>{RATING_LABELS[lvl]}</span>
                </button>
              ))}
              <button
                type='button'
                className='snap-req-picker__opt'
                role='menuitem'
                onClick={() => {
                  onSet('deprecated');
                  setOpen(false);
                }}
              >
                <span
                  className='snap-req-picker__swatch snap-cell is-deprecated'
                  aria-hidden
                />
                <span>Deprecate</span>
              </button>
              {level ? (
                <button
                  type='button'
                  className='snap-req-picker__opt snap-req-picker__opt--clear'
                  role='menuitem'
                  onClick={() => {
                    onSet(null);
                    setOpen(false);
                  }}
                >
                  <span
                    className='snap-req-picker__swatch is-clear'
                    aria-hidden
                  />
                  <span>Clear</span>
                </button>
              ) : null}
            </>
          ) : (
            <>
              <button
                type='button'
                className='snap-req-picker__opt snap-req-picker__opt--back'
                role='menuitem'
                onClick={() => setView('levels')}
              >
                ← Back
              </button>
              <div className='snap-req-picker__divider' />
              {(availableFunctions ?? []).map(fn => {
                const checked = assignedFunctionIds?.has(fn.id) ?? false;
                return (
                  <label key={fn.id} className='snap-req-picker__opt'>
                    <input
                      type='checkbox'
                      className='snap-req-picker__checkbox'
                      checked={checked}
                      onChange={() => onToggleFunction?.(fn.id)}
                    />
                    <span>{fn.display}</span>
                  </label>
                );
              })}
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
